<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from '@/components/ui/Button.vue'

const { t } = useI18n()

const provider = ref('google')
const logoPath = ref('')
const orgName = ref('')
const loading = ref(true)

onMounted(async () => {
  try {
    // Pass the current hostname to get correct provider (proxy changes host header)
    const currentHost = window.location.hostname
    const res = await fetch(`/api/auth/config?host=${encodeURIComponent(currentHost)}`)
    if (res.ok) {
      const config = await res.json()
      provider.value = config.provider || 'google'
      logoPath.value = config.logo_path || '/assets/bixbit.png'
      orgName.value = config.org_name || ''
    }
  } catch (e) {
    console.error('Failed to fetch auth config:', e)
  } finally {
    loading.value = false
  }
})

const login = () => {
  // Pass provider and origin to backend for OAuth state
  const origin = encodeURIComponent(window.location.origin)
  window.location.href = `/api/auth/login?provider=${provider.value}&origin=${origin}`
}

const buttonText = computed(() => {
  if (provider.value === 'microsoft') {
    return t('login.buttonMicrosoft')
  }
  return t('login.buttonGoogle')
})

const providerInfo = computed(() => {
  if (provider.value === 'microsoft') {
    return {
      text: t('login.microsoftInfo'),
      subtext: t('login.microsoftSubtext')
    }
  }
  return {
    text: t('login.googleInfo'),
    subtext: t('login.googleSubtext')
  }
})
</script>

<template>
  <div class="flex flex-col md:flex-row h-full overflow-hidden" style="padding-top: 2.5rem;">
    <!-- Left panel: dark slate in light mode, white in dark mode — hidden on mobile -->
    <div class="hidden md:flex md:w-1/2 relative flex-col bg-slate-800 dark:bg-white">
      <!-- Organization name -->
      <div v-if="orgName && provider === 'microsoft'" class="absolute top-6 right-6 text-white dark:text-slate-900 text-right font-bold text-xl leading-tight">
        <div v-for="(word, index) in orgName.split(' ')" :key="index">{{ word }}</div>
      </div>

      <!-- Guide + centered org name -->
      <div class="flex-1 flex items-center justify-center relative px-10">
        <div v-if="orgName && provider !== 'microsoft'" class="absolute top-10 text-white dark:text-slate-900 font-bold text-4xl tracking-wider">
          {{ orgName }}
        </div>

        <!-- User guide -->
        <div class="relative z-10 space-y-6 max-w-sm">
          <h2 class="text-xl font-semibold mb-4 text-white dark:text-slate-900">{{ $t('guide.title') }}</h2>

          <div class="flex items-start gap-3">
            <span class="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 dark:bg-black/10 flex items-center justify-center text-sm font-bold text-white dark:text-slate-800">1</span>
            <div>
              <p class="font-medium text-white dark:text-slate-800">{{ $t('guide.step1title') }}</p>
              <p class="text-sm text-white/70 dark:text-slate-600">{{ $t('guide.step1desc') }}</p>
            </div>
          </div>

          <div class="flex items-start gap-3">
            <span class="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 dark:bg-black/10 flex items-center justify-center text-sm font-bold text-white dark:text-slate-800">2</span>
            <div>
              <p class="font-medium text-white dark:text-slate-800">{{ $t('guide.step2title') }}</p>
              <p class="text-sm text-white/70 dark:text-slate-600">{{ $t('guide.step2desc') }}</p>
            </div>
          </div>

          <div class="flex items-start gap-3">
            <span class="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 dark:bg-black/10 flex items-center justify-center text-sm font-bold text-white dark:text-slate-800">3</span>
            <div>
              <p class="font-medium text-white dark:text-slate-800">{{ $t('guide.step3title') }}</p>
              <p class="text-sm text-white/70 dark:text-slate-600">{{ $t('guide.step3desc') }}</p>
            </div>
          </div>

          <div class="flex items-start gap-3">
            <span class="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 dark:bg-black/10 flex items-center justify-center text-sm font-bold text-white dark:text-slate-800">4</span>
            <div>
              <p class="font-medium text-white dark:text-slate-800">{{ $t('guide.step4title') }}</p>
              <p class="text-sm text-white/70 dark:text-slate-600">{{ $t('guide.step4desc') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right panel: white in light mode, dark navy in dark mode — full width on mobile -->
    <div class="flex-1 flex items-center justify-center px-6 md:px-8 bg-white dark:bg-[#0f172a]">
      <div class="max-w-md w-full text-center">
        <!-- Company logo above button -->
        <div class="mb-8 flex justify-center">
          <img
            v-if="logoPath"
            :src="logoPath"
            alt="Company Logo"
            class="h-20 object-contain"
          />
        </div>

        <!-- Login button -->
        <div v-if="!loading">
          <!-- Microsoft button -->
          <Button
            v-if="provider === 'microsoft'"
            variant="default"
            class="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3 text-base flex items-center justify-center"
            @click="login"
          >
            <svg class="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="10" height="10" fill="currentColor"/>
              <rect x="11" width="10" height="10" fill="currentColor"/>
              <rect y="11" width="10" height="10" fill="currentColor"/>
              <rect x="11" y="11" width="10" height="10" fill="currentColor"/>
            </svg>
            {{ buttonText }}
          </Button>

          <!-- Google button -->
          <Button
            v-else
            variant="outline"
            class="w-full py-3 text-base"
            style="background-color: white; color: #374151; border-color: #d1d5db;"
            @click="login"
          >
            <svg class="w-5 h-5 mr-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C39.712,35.619,44,29.831,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
            </svg>
            {{ buttonText }}
          </Button>
        </div>

        <!-- Loading state -->
        <div v-else class="flex justify-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>

        <!-- Provider info text -->
        <div class="mt-6 text-sm text-slate-500 dark:text-slate-400">
          <p>{{ providerInfo.text }}</p>
          <p class="text-xs mt-1">{{ providerInfo.subtext }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
